import { LinkButton, useParsedSearchParams } from "@fidelix/fx-miranda";
import { createSearchParams, useSearchParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { Trans } from "@lingui/react/macro";
import { serializeHistoryParams } from "~utils/parse";
import { Point, PointType } from "../point/point.model";
import { useLocationStore, useNavigatonPath } from "../utils/url";
import { useGraphicsContext } from "./graphics-context";
import { usePointGroupsQuery } from "../pointgroup/point-group.queries";
import { ViewGroupHistoryButton } from "../point/point-history/view-group-history-button";
import { MAX_VISIBLE_POINTS } from "../point/point-history/point-history.constants";
import { PointGroup } from "../pointgroup/point-group.model";
import { useGraphicsPointGroupInfoQuery } from "./graphics.queries";
import { useUserContext } from "../user/user-context";
import { hasPermission } from "../utils/permission-helper";
import { Permissions } from "../const";

const historyPointTypes = [
  PointType.DI,
  PointType.DO,
  PointType.AI,
  PointType.AO,
  PointType.Control,
];

export function GraphicsHeaderButtons() {
  const { pointsQuery, graphics } = useGraphicsContext();
  const { user } = useUserContext();

  const hasEditPermission = hasPermission(
    Permissions.POINTGROUPS_EDIT,
    user?.permissions || [],
  );

  const { parsedParams } = useParsedSearchParams({
    graphic: { type: "string", defaultValue: "" },
    graphicsId: { type: "number" },
  });

  const { data: groupsData, isFetching: isGroupsFetching } =
    usePointGroupsQuery(
      {
        page: 1,
        pageSize: 1,
        withValues: false,
        graphicId: parsedParams.graphicsId,
        graphicLink: parsedParams.graphic,
      },
      {
        enabled: !!parsedParams.graphicsId && !!parsedParams.graphic,
      },
    );

  const { data: groupInfo, isFetching: isGroupInfoFetching } =
    useGraphicsPointGroupInfoQuery(
      {
        graphics: graphics!,
        path: parsedParams.graphic,
      },
      {
        enabled:
          hasEditPermission &&
          !!graphics &&
          !isGroupsFetching &&
          groupsData?.groups.length === 0,
      },
    );

  if (isGroupsFetching) {
    return <LoadingContainer />;
  }

  const filteredPoints = pointsQuery.data?.points.filter((p) =>
    historyPointTypes.includes(p.type),
  );

  return (
    <Buttons
      filteredPoints={filteredPoints || []}
      isPointsFetching={pointsQuery.isFetching}
      isGroupInfoFetching={isGroupInfoFetching}
      groups={groupsData?.groups || []}
      groupInfo={groupInfo}
      hasEditPermission={hasEditPermission}
      graphicId={parsedParams.graphicsId}
      graphicLink={parsedParams.graphic}
    />
  );
}

type ButtonProps = {
  filteredPoints: Point[];
  isPointsFetching: boolean;
  isGroupInfoFetching: boolean;
  groups: PointGroup[];
  groupInfo: ReturnType<typeof useGraphicsPointGroupInfoQuery>["data"];
  graphicLink?: string;
  graphicId?: number;
  hasEditPermission: boolean;
};

function Buttons({
  filteredPoints,
  isPointsFetching,
  isGroupInfoFetching,
  groups,
  groupInfo,
  graphicId,
  graphicLink,
  hasEditPermission,
}: ButtonProps) {
  const { to } = useNavigatonPath();
  const { setLocation } = useLocationStore();
  const setSearchParams = useSearchParams()[1];

  // Ensure that measurement points take precedence in visibility over all other point types
  const prioritizedPoints = [...filteredPoints].sort(
    (a, b) => Number(b.type === PointType.AI) - Number(a.type === PointType.AI),
  );

  // Convert points to base64 since that is the format history view uses
  const selectedHistoryPoints = filteredPoints.length
    ? serializeHistoryParams({
        points: prioritizedPoints.reduce<
          Record<number, Record<string, number>>
        >(
          // Set visibility to
          // 1 = true for the first MAX_VISIBLE_POINTS points
          // 0 = false to the rest of the points.
          (a, c, i) => ({
            ...a,
            [c.id]: { visible: i < MAX_VISIBLE_POINTS ? 1 : 0 },
          }),
          {},
        ),
        groups: {},
        limits: {},
      })
    : undefined;

  const onPress = () => {
    setSearchParams((params) => {
      params.delete("replay");
      return params;
    });
    setLocation("back-to-graphics");
  };

  return (
    <ButtonsContainer>
      {hasEditPermission && groups.length === 0 && (
        <HistoryButton
          variant="secondary"
          isDisabled={isGroupInfoFetching}
          onPress={onPress}
          to={to(
            `pointgroups/create?${createSearchParams({
              selected: selectedHistoryPoints ?? "",
              from: "graphics",
              ...(groupInfo && { defaultName: groupInfo }),
              ...(graphicLink && { graphicLink }),
              ...(graphicId && { graphicId: String(graphicId) }),
            }).toString()}`,
          )}
        >
          <Trans>Create group</Trans>
        </HistoryButton>
      )}
      {groups.length > 0 ? (
        <ViewGroupHistoryButton
          variant="secondary"
          onPressUp={onPress}
          pointGroups={groups}
        >
          <Trans>View history</Trans>
        </ViewGroupHistoryButton>
      ) : (
        <HistoryButton
          isDisabled={
            !filteredPoints || filteredPoints.length === 0 || isPointsFetching
          }
          isLoading={isPointsFetching}
          iconPlacement="left"
          variant="secondary"
          onPress={onPress}
          to={to(
            `history?${createSearchParams({
              selected: selectedHistoryPoints ?? "",
              from: "graphics",
            }).toString()}`,
          )}
        >
          <Trans>View history</Trans>
        </HistoryButton>
      )}
    </ButtonsContainer>
  );
}

const shimmerAnimation = keyframes`
  0% {
    background-position: -800px 0;
  }
  100% {
    background-position: 800px 0;
  }
`;

const shimmer = css`
  animation: ${shimmerAnimation} 1.5s ease-in-out infinite;
  background-repeat: no-repeat;
  background-size: 1250px 100%;
  background-color: ${(p) => p.theme.colors.mutedLightNeutral};
  background-image: linear-gradient(
    90deg,
    ${(p) => p.theme.colors.mutedLightNeutral} 0%,
    ${(p) => p.theme.colors.mutedLightNeutral} 35%,
    ${(p) => p.theme.colors.lightNeutral} 50%,
    ${(p) => p.theme.colors.mutedLightNeutral} 65%,
    ${(p) => p.theme.colors.mutedLightNeutral} 100%
  );
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${(p) => p.theme.space("small")};
`;

const LoadingContainer = styled.div`
  ${shimmer}
  min-width: 0px;
  width: 200px;
  height: 48px;
  border-radius: ${(p) => p.theme.radius("large")};
  flex-shrink: 1;
`;
const HistoryButton = styled(LinkButton)`
  white-space: nowrap;
`;
